// Rotas do app CRM | CS.
//
// URL LIMPA no domínio próprio (/medicoes, /conta/4821) com a CHAVE de ACL em
// meta.permissionPath — os paths /crm/cs* do catálogo global `routes`
// (migration 190). A lista canônica vem de externalAppRoutes.ts, o MESMO
// módulo que o syncFrontendRoutes do Hub inclui no payload: os dois lados não
// têm como divergir.
import type { RouteRecordRaw } from 'vue-router';
import { CRM_CS_ROUTES } from '@/router/externalAppRoutes';

const PERM = Object.fromEntries(CRM_CS_ROUTES.map((r) => [r.name, r.path])) as Record<string, string>;

export const crmRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'CrmLogin',
    // ⚠ É O LOGIN DO HUB, o mesmo componente (pedido de 07/08/2026): quem
    // entra direto pelo domínio do CS vê a MESMA porta de entrada e cai no
    // CRM, sem passar pelo Hub e trocar de app na mão. Funciona porque o
    // `SsoLogin` manda `app_host` e o callback devolve o token para o host de
    // origem — e porque a tela do Hub não usa Vuetify (é Vue puro + Iconify),
    // então cabe no bundle do CRM sem arrastar o design system antigo.
    // A `views/Auth/CrmLogin.vue` foi apagada: duas portas de entrada com
    // visual diferente para o mesmo login era o que se queria eliminar.
    component: () => import('@/views/Login/Login.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/login/sso-callback',
    name: 'CrmSsoCallback',
    component: () => import('../views/Auth/CrmSsoCallback.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: () => import('../layouts/CrmLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      // Home e Carteira compartilham a MESMA chave de ACL (/crm/cs): são duas
      // visões do mesmo dado e não há linha própria em `routes` para a Home —
      // criar uma exigiria migration e daria a alguém a chance de conceder
      // acesso ao painel sem a carteira, que não faz sentido.
      {
        path: '',
        name: 'CrmHome',
        component: () => import('../views/Home/CrmHome.vue'),
        meta: { title: 'Painel', permissionPath: PERM['crm-cs-carteira'] },
      },
      {
        // ⚠ A URL mudou de `/carteira` para `/clientes` (15/08/2026): "Carteira"
        // passou a ser a tela de MONTAGEM, e dois itens de menu com o mesmo
        // nome apontando para telas diferentes é confusão permanente. A CHAVE
        // DE ACL não mudou (`/crm/cs`) — URL limpa não é chave de permissão, e
        // trocá-la exigiria migration e reconcessão para todo mundo.
        path: 'clientes',
        name: 'CrmClientes',
        component: () => import('../views/Carteira/CarteiraPage.vue'),
        meta: { title: 'Clientes', permissionPath: PERM['crm-cs-carteira'] },
      },
      {
        // `/carteira` antiga continua respondendo, redirecionando: há link
        // colado em conversa e aba salva apontando para ela, e devolver 404
        // para quem tinha o endereço certo ontem é ruído evitável.
        path: 'carteira',
        redirect: { name: 'CrmClientes' },
      },
      {
        // A MONTAGEM. Chave de ACL própria: `ver` neste nó dispensa o recorte
        // por responsável no módulo inteiro.
        path: 'montagem',
        name: 'CrmMontagem',
        component: () => import('../views/Montagem/MontagemPage.vue'),
        meta: { title: 'Carteira', permissionPath: PERM['crm-cs-montagem'] },
      },
      {
        path: 'conta/:id',
        name: 'CrmConta',
        component: () => import('../views/Conta/ContaPage.vue'),
        meta: { title: 'Ficha da Escola', permissionPath: PERM['crm-cs-conta'] },
      },
      // ── Criar contrato de venda (página autônoma) ─────────────────
      // A escola é escolhida DENTRO da página; `?conta=` a pré-seleciona quando
      // se chega pela ficha. ACL da ficha, função `ficha.contrato.criar`.
      {
        path: 'contratos/novo',
        name: 'CrmContratoNovo',
        component: () => import('../views/Contrato/ContratoNovoPage.vue'),
        meta: { title: 'Criar contrato de venda', permissionPath: PERM['crm-cs-conta'] },
      },
      {
        path: 'medicoes',
        name: 'CrmMedicoes',
        component: () => import('../views/Medicoes/MedicoesPage.vue'),
        meta: { title: 'Medições', permissionPath: PERM['crm-cs-medicoes'] },
      },
      // ── Separação (logística) ─────────────────────────────────────
      // MESMA chave de ACL da fila de medições: é a continuação do mesmo fluxo
      // (a medição vira pedido, o pedido vira ordem de separação) e chave nova
      // exigiria conceder duas coisas para acompanhar um processo só. O recorte
      // fino é do nó `separacao`, na matriz do CRM.
      {
        path: 'separacao',
        name: 'CrmSeparacao',
        component: () => import('../views/Separacao/SeparacaoPage.vue'),
        meta: { title: 'Separação', permissionPath: PERM['crm-cs-medicoes'] },
      },
      // ── Tarefas (kanban da carteira) ──────────────────────────────
      // Mesma chave de ACL da ficha, pela mesma razão do Calendário: são as
      // tarefas das escolas que a pessoa já vê lá, agrupadas por carteira.
      {
        path: 'tarefas',
        name: 'CrmTarefas',
        component: () => import('../views/Tarefas/TarefasPage.vue'),
        meta: { title: 'Tarefas', permissionPath: PERM['crm-cs-conta'] },
      },

      // ── Calendário (agenda do analista) ───────────────────────────
      // Reusa a chave de ACL da FICHA (`/crm/cs/conta`): a agenda mostra as
      // tarefas e reuniões das escolas que a pessoa já vê na ficha — chave nova
      // exigiria conceder duas coisas para ver o mesmo dado reorganizado. O
      // recorte fino é do nó `calendario`, na matriz.
      {
        path: 'calendario',
        name: 'CrmCalendario',
        component: () => import('../views/Calendario/CalendarioPage.vue'),
        meta: { title: 'Calendário', permissionPath: PERM['crm-cs-conta'] },
      },

      // ── Chamados ──────────────────────────────────────────────────
      // Chave de ACL própria (`/crm/cs/chamados`, migration 249): a fila e a
      // carteira têm públicos diferentes. As três telas dividem a MESMA chave
      // — abrir e responder são o mesmo trabalho da fila, e separá-las daria
      // a alguém a chance de conceder a lista sem o chamado que ela abre.
      {
        path: 'chamados',
        name: 'CrmChamados',
        component: () => import('../views/Chamados/ChamadosPage.vue'),
        meta: { title: 'Chamados', permissionPath: PERM['crm-cs-chamados'] },
      },
      {
        // ANTES de `chamados/:id`: sem esta ordem, "novo" casaria como id e a
        // tela de abertura viraria um detalhe inexistente.
        path: 'chamados/novo',
        name: 'CrmChamadoNovo',
        component: () => import('../views/Chamados/ChamadoNovoPage.vue'),
        meta: { title: 'Abrir chamado', permissionPath: PERM['crm-cs-chamados'] },
      },
      {
        path: 'chamados/:id',
        name: 'CrmChamadoDetalhe',
        component: () => import('../views/Chamados/ChamadoDetalhePage.vue'),
        meta: { title: 'Chamado', permissionPath: PERM['crm-cs-chamados'] },
      },
      // ── Reports ───────────────────────────────────────────────────
      // Chave de ACL própria (`/crm/cs/reports`, migration 285): página de
      // análise da integração plataforma ↔ ERP, público distinto da carteira.
      {
        path: 'reports',
        name: 'CrmReports',
        component: () => import('../views/Reports/ReportsPage.vue'),
        meta: { title: 'Reports', permissionPath: PERM['crm-cs-reports'] },
      },
      {
        // Assistente de relatórios: mesma ACL do Reports; o nó `reports.personalizados`
        // é conferido na própria tela (estado "sem acesso").
        path: 'relatorios',
        name: 'CrmRelatoriosAssistente',
        component: () => import('../views/Relatorios/RelatoriosAssistentePage.vue'),
        meta: { title: 'Relatórios', permissionPath: PERM['crm-cs-reports'] },
      },
      {
        path: 'admin/sync',
        name: 'CrmSync',
        component: () => import('../views/Admin/CrmSyncPage.vue'),
        meta: { title: 'Sincronização', permissionPath: PERM['crm-cs-sync'] },
      },
      {
        // Chave própria (`/crm/cs/admin/parametros`, migration 345), nível `admin` na API.
        path: 'admin/parametros',
        name: 'CrmParametros',
        component: () => import('../views/Admin/CrmParametrosPage.vue'),
        meta: { title: 'Parâmetros do Protheus', permissionPath: PERM['crm-cs-parametros'] },
      },
      {
        // Chave PRÓPRIA (`/crm/cs/permissoes`, migration 214) e nível `admin`
        // no backend. O guard daqui só sabe negar `none` — quem exige o nível
        // é `requireRouteAccess` na API; o item do menu já filtra por `admin`.
        path: 'permissoes',
        name: 'CrmPermissoes',
        component: () => import('../views/Permissoes/PermissoesPage.vue'),
        meta: { title: 'Permissões', permissionPath: PERM['crm-cs-permissoes'] },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'CrmNotFound',
    component: () => import('../views/CrmNotFound.vue'),
    meta: { requiresAuth: false },
  },
];
